import React, { useCallback, useEffect, useState } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  IconButton,
  Paper,
  Typography,
} from '@mui/material';
import VisibilityOutlinedIcon from '@mui/icons-material/VisibilityOutlined';
import { DataGrid, GridColDef } from '@mui/x-data-grid';
import { format } from 'date-fns';
import api from '../services/api';
import StaffNewOrderDialog from './StaffNewOrderDialog';

interface AccountOrdersTabProps {
  accountId: string;
}

const AccountOrdersTab: React.FC<AccountOrdersTabProps> = ({ accountId }) => {
  const [rows, setRows] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [newOrderOpen, setNewOrderOpen] = useState(false);

  const load = useCallback(async () => {
    if (!accountId) return;
    setLoading(true);
    setError(null);
    try {
      const data = await api.adminListOrders({
        account_id: accountId,
        page: 1,
        per_page: 100,
      });
      setRows(Array.isArray(data) ? data : []);
    } catch (e: unknown) {
      setError((e as Error)?.message || 'Failed to load orders');
      setRows([]);
    } finally {
      setLoading(false);
    }
  }, [accountId]);

  useEffect(() => {
    load();
  }, [load]);

  const formatMoney = (price: unknown) => {
    const n = typeof price === 'number' && Number.isFinite(price) ? price : Number(price ?? 0);
    const safe = Number.isFinite(n) ? n : 0;
    return `$${safe.toFixed(2)}`;
  };

  const formatCellDateTime = (value: unknown) => {
    if (value == null || value === '') return '—';
    const d = value instanceof Date ? value : new Date(String(value));
    if (Number.isNaN(d.getTime())) return '—';
    return format(d, 'PPp');
  };

  const columns: GridColDef[] = [
    { field: 'order_reference', headerName: 'Order ref', width: 150 },
    {
      field: 'status',
      headerName: 'Status',
      width: 120,
      renderCell: (params) => <Chip label={params.value} size="small" variant="outlined" />,
    },
    {
      field: 'customer_name',
      headerName: 'Customer',
      width: 200,
      valueGetter: (params) =>
        params.row.customer_name ||
        `${params.row.first_name || ''} ${params.row.last_name || ''}`.trim(),
    },
    { field: 'email', headerName: 'Email', width: 220 },
    {
      field: 'total_monthly',
      headerName: 'Monthly',
      width: 100,
      renderCell: (params) => formatMoney(params.value),
    },
    {
      field: 'total_one_time',
      headerName: 'One-time',
      width: 100,
      renderCell: (params) => formatMoney(params.value),
    },
    {
      field: 'created_at',
      headerName: 'Created',
      width: 180,
      renderCell: (params) => formatCellDateTime(params.value),
    },
    {
      field: 'actions',
      headerName: 'Actions',
      width: 100,
      sortable: false,
      renderCell: (params) => (
        <IconButton
          size="small"
          component={RouterLink}
          to={`/admin/orders?openOrder=${params.row.id}`}
          aria-label="View order in Order management"
        >
          <VisibilityOutlinedIcon fontSize="small" />
        </IconButton>
      ),
    },
  ];

  return (
    <Paper variant="outlined" sx={{ p: 2 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2, flexWrap: 'wrap', gap: 1 }}>
        <Typography variant="h6" fontWeight={600}>
          Orders
        </Typography>
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Button size="small" variant="contained" onClick={() => setNewOrderOpen(true)}>
            New order
          </Button>
          <Button size="small" variant="outlined" onClick={() => load()} disabled={loading}>
            Refresh
          </Button>
        </Box>
      </Box>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Orders linked to this account. Use the <strong>view</strong> action to open the full order in{' '}
        <strong>Order management</strong>, or <strong>New order</strong> to add a staff catalog line. Unassigned orders
        are listed under <strong>Orders → Order management</strong> with &quot;Unassigned only&quot;.
      </Typography>
      <StaffNewOrderDialog
        open={newOrderOpen}
        onClose={() => setNewOrderOpen(false)}
        onSuccess={() => void load()}
        defaultAccountId={accountId}
        lockAccount
      />
      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}
      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
          <CircularProgress size={32} />
        </Box>
      ) : (
        <DataGrid
          rows={rows}
          columns={columns}
          autoHeight
          hideFooter={rows.length <= 10}
          pageSizeOptions={[10, 25, 50]}
          initialState={{ pagination: { paginationModel: { pageSize: 10, page: 0 } } }}
          disableRowSelectionOnClick
          getRowId={(r) => r.id}
        />
      )}
    </Paper>
  );
};

export default AccountOrdersTab;
